import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { SettingsService } from 'src/app/core/settings/settings.service';
import { ReportsService } from '../reports.service';
import { GlobalFunctionsService } from '../../common/functions/global-functions.service';

// Modelos
import { UserLogin } from '../../common/models/UserLogin.Model';
import { Unit } from '../../common/models/Unit.Model';

@Component({
  selector: 'app-rep-container-exchange',
  templateUrl: './rep-container-exchange.component.html',
  styleUrls: ['./rep-container-exchange.component.scss'],
  providers: [DatePipe]
})
export class RepContainerExchangeComponent implements OnInit {

  // Estado de carga
  public downloadingDetail = false;
  public downloadingTotals = false;
  public user: UserLogin;
  public nodes: Unit[] = [];
  public maxDate: string;

  // Flags de validación (vienen de los aph-custom-date-picker)
  public isValidDetailLabel = true;
  public isValidDetailRem = true;
  public isValidTotalRem = true;

  // Variables para Reporte DETALLE
  public detailOrigin: Unit;
  public detailDestination: Unit;
  public detailLabel = '';
  public detailLabelStartDate = '';
  public detailLabelEndDate = '';
  public detailRem = '';
  public detailRemStartDate = '';
  public detailRemEndDate = '';

  // Variables para Reporte TOTALES
  public totalOrigin: Unit;
  public totalDestination: Unit;
  public totalRem = '';
  public totalRemStartDate = '';
  public totalRemEndDate = '';

  constructor(
    private settings: SettingsService,
    private reportService: ReportsService,
    private globalService: GlobalFunctionsService,
    private datePipe: DatePipe
  ) { 
    // Establecer fecha máxima (hoy)
    this.maxDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd')!;
  }

  async ngOnInit() {
    this.user = this.settings.getUser();
    
    // Verificaciones de seguridad/permisos
    this.globalService.canOperate();
    this.globalService.hasFSBRPending();
    
    this.initializeDates();
    await this.getUserNodes();
  }

  /**
   * Inicializa las fechas por defecto al día de hoy
   */
  private initializeDates(): void {
    const today = this.maxDate;
    this.detailLabelStartDate = today;
    this.detailLabelEndDate = today;
    this.detailRemStartDate = today;
    this.detailRemEndDate = today;
    this.totalRemStartDate = today;
    this.totalRemEndDate = today;
  }

  /**
   * Obtiene los nodos (CEVE/FAB) asignados al usuario
   */
  async getUserNodes() {
    try {
      this.reportService.getAllUserNodes(this.user.Org, this.user.Id).subscribe({
        next: (resp: Unit[]) => {
          if (resp && resp.length > 0) {
            this.nodes = resp.filter(x => x.LevelType === 'CEVE' || x.LevelType === "FAB");
            // Agregar opción "Todos" al inicio
            this.nodes.unshift({ 
              UnitId: 0, 
              UnitDescription: 'Todos',
              OrgId: 0,
              UnitOrgId: 0
            } as Unit);
          } else {
            this.nodes = [{ UnitDescription: 'No tiene Centros de ventas asignados' } as Unit];
          }
          this.setDefaultSelection();
        },
        error: (err) => this.showErrorMessage(err)
      });
    } catch (err) {
      this.showErrorMessage(err);
    }
  }

  private setDefaultSelection(): void {
    this.detailOrigin = this.nodes[0];
    this.detailDestination = this.nodes[0];
    this.totalOrigin = this.nodes[0];
    this.totalDestination = this.nodes[0];
  }

  // --- Manejadores de Autocomplete ---
  detailOriginCleared() { this.detailOrigin = this.nodes[0]; }
  detailDestinationCleared() { this.detailDestination = this.nodes[0]; }
  totalOriginCleared() { this.totalOrigin = this.nodes[0]; }
  totalDestinationCleared() { this.totalDestination = this.nodes[0]; }
  
  // Eventos de selección (actualmente vacíos por lógica de negocio, pero declarados por el componente)
  detailOriginSelected() {}
  detailDestinationSelected() {}
  totalOriginSelected() {}
  totalDestinationSelected() {}

  /**
   * Ejecuta la descarga del reporte de DETALLE
   */
  public downloadDetail(): void {
    if (!this.validateSelection(this.detailOrigin, this.detailDestination)) return;

    this.downloadingDetail = true;
    const params = {
      type: 'D',
      originId: this.detailOrigin.UnitId,
      destId: this.detailDestination.UnitId,
      lbl: this.detailLabel || '0',
      lblStart: this.detailLabelStartDate || '2000-01-01',
      lblEnd: this.detailLabelEndDate || '2000-01-01',
      rem: this.detailRem || '0',
      remStart: this.detailRemStartDate || '2000-01-01',
      remEnd: this.detailRemEndDate || '2000-01-01'
    };

    this.executeReportRequest(params, 'Reporte detalle cambio de equipo');
  }

  /**
   * Ejecuta la descarga del reporte de TOTALES
   */
  public downloadTotal(): void {
    if (!this.validateSelection(this.totalOrigin, this.totalDestination)) return;

    this.downloadingTotals = true;
    const params = {
      type: 'T',
      originId: this.totalOrigin.UnitId,
      destId: this.totalDestination.UnitId,
      lbl: '0',
      lblStart: '2000-01-01',
      lblEnd: '2000-01-01',
      rem: this.totalRem || '0',
      remStart: this.totalRemStartDate || '2000-01-01',
      remEnd: this.totalRemEndDate || '2000-01-01'
    };

    this.executeReportRequest(params, 'Reporte totales cambio de equipo');
  }

  /**
   * Lógica común para peticiones de descarga de Excel
   */
  private executeReportRequest(p: any, fileName: string): void {
    this.reportService.containerExchangeReport(
      p.type, p.originId, p.destId, p.lbl, p.lblStart, p.lblEnd, p.rem, p.remStart, p.remEnd
    ).subscribe({
      next: (resp: any) => {
        if (!resp) {
          this.globalService.messageToast('No se encontró información.', 'warning');
        } else {
          this.downloadBlob(resp, fileName);
        }
        this.resetLoadingFlags();
      },
      error: (err) => {
        this.showErrorMessage(err);
        this.resetLoadingFlags();
      }
    });
  }

  private downloadBlob(resp: any, name: string): void {
    const blob = new Blob([resp], { type: resp.type });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${name}.xlsx`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  private resetLoadingFlags(): void {
    this.downloadingDetail = false;
    this.downloadingTotals = false;
  }

  /**
   * Valida que se haya seleccionado un origen y destino válidos
   */
  private validateSelection(origin: Unit, dest: Unit): boolean {
    if (!origin || isNaN(origin.UnitId)) {
      this.showErrorMessage('Debe seleccionar un centro de ventas de origen.');
      return false;
    }
    if (!dest || isNaN(dest.UnitId)) {
      this.showErrorMessage('Debe seleccionar un centro de ventas de destino.');
      return false;
    }
    return true;
  }

  /**
   * Muestra mensajes de error estandarizados
   */
  public async showErrorMessage(err: any) {
    console.error('Error en reporte:', err);
    const message = err?.error?.Message || err?.message || err || 'Ocurrió un error inesperado';
    await this.globalService.messageBimbo(message, 'warning', '¡Alerta!');
  }
}